import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Ban, Check, ChevronDown, Crown, Flag, Gift, MessageSquare, Pencil, Plus, Radio, Share2, Sparkles, Video } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { requestVipCheck } from "@/components/vip/VipAwardAnnouncer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import GiftPicker from "@/components/GiftPicker";
import ReportModal from "@/components/ReportModal";
import BlockConfirmDialog from "@/components/BlockConfirmDialog";
import VerifiedBadge from "@/components/VerifiedBadge";
import { useControlCenter } from "@/context/ControlCenterContext";
import { Shield, Camera } from "lucide-react";
import AmbassadorBadge from "@/components/AmbassadorBadge";
import VipBadge from "@/components/VipBadge";
import LifetimeBossBadge from "@/components/LifetimeBossBadge";
import TopBadge from "@/components/TopBadge";

/** PandaX header art — bamboo-green → rose dusk with a panda-ear silhouette. Used when no cover image is set. */
export function PandaCover({ className = "" }) {
  return (
    <div className={`relative overflow-hidden bg-[#0b0f0e] ${className}`} aria-hidden>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_20%,rgba(16,185,129,0.28),transparent_55%),radial-gradient(ellipse_at_85%_70%,rgba(244,63,94,0.28),transparent_50%)]" />
      <svg className="absolute -right-6 -bottom-10 w-56 h-56 opacity-[0.14]" viewBox="0 0 200 200" fill="white">
        <circle cx="52" cy="58" r="26" /><circle cx="148" cy="58" r="26" />
        <ellipse cx="100" cy="112" rx="72" ry="66" />
        <ellipse cx="72" cy="104" rx="16" ry="20" fill="#0b0f0e" transform="rotate(-18 72 104)" />
        <ellipse cx="128" cy="104" rx="16" ry="20" fill="#0b0f0e" transform="rotate(18 128 104)" />
        <ellipse cx="100" cy="138" rx="12" ry="8" fill="#0b0f0e" />
      </svg>
      <div className="absolute inset-0 opacity-[0.06] bg-[repeating-linear-gradient(115deg,white_0_1px,transparent_1px_14px)]" />
    </div>
  );
}

function Badges({ profile }) {
  const items = [];
  if (profile.is_creator) items.push({ k: "creator", label: "Creator", Icon: Video, cls: "text-rose-300 border-rose-500/40" });
  if (profile.referral_featured) items.push({ k: "featured", label: "Featured", Icon: Crown, cls: "text-amber-200 border-amber-300/40" });
  if (profile.is_live) items.push({ k: "live", label: "Live now", Icon: Radio, cls: "text-red-300 border-red-500/50 animate-pulse" });
  const hasAwardBadge = profile.top_badge || profile.lifetime_boss || !!profile.vip_tier;
  if (!items.length && profile.ambassador_status !== "approved" && !hasAwardBadge) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="profile-badges">
      {items.map(({ k, label, Icon, cls }) => (
        <span key={k} data-testid={`profile-badge-${k}`} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border bg-black/40 ${cls}`}><Icon size={10} /> {label}</span>
      ))}
      <AmbassadorBadge status={profile.ambassador_status} tier={profile.ambassador_tier} />
      <LifetimeBossBadge show={profile.lifetime_boss} />
      <VipBadge tier={profile.vip_tier} />
      <TopBadge show={profile.top_badge} />
    </div>
  );
}

/** Public profile hero — photo, PandaX header, identity, badges, bio, counts, actions. No dashboard boxes. */
export default function ProfileHero({ profile, isMe, me, onReload, onCreatePost }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [giftOpen, setGiftOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const cc = useControlCenter();
  const [busy, setBusy] = useState(false);

  const toggleFollow = async () => {
    if (!me) return navigate("/login");
    setBusy(true);
    try {
      if (profile.is_followed_by_me) await api.delete(`/users/${profile.id}/follow`);
      else await api.post(`/users/${profile.id}/follow`);
      toast.success(profile.is_followed_by_me ? `Unfollowed ${profile.display_name}` : `Following ${profile.display_name}`);
      onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update follow."); } finally { setBusy(false); }
  };
  const [blockOpen, setBlockOpen] = useState(false);
  const toggleBlock = async () => {
    if (!profile.is_blocked_by_me) { setBlockOpen(true); return; }
    try {
      await api.delete(`/users/${profile.id}/block`);
      toast.success("Unblocked.");
      onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update block."); }
  };
  useEffect(() => {
    if (!isMe && me && new URLSearchParams(location.search).get("gift") === "1") setGiftOpen(true);
  }, [isMe, me, location.search]);
  const sendGift = async (giftKey, message) => {
    await api.post(`/users/${profile.handle}/gift`, { gift_key: giftKey, message });
    requestVipCheck();
    toast.success(`Gift sent to ${profile.display_name}`);
    onReload?.();
  };
  const share = async () => {
    const url = `${window.location.origin}/u/${profile.id}`;
    try { if (navigator.share) await navigator.share({ title: `${profile.display_name} on My Panda X`, url }); else { await navigator.clipboard.writeText(url); toast.success("Profile link copied."); } } catch { /* cancelled */ }
  };
  const name = profile.display_name || profile.handle;

  return (
    <section data-testid="profile-hero" className="relative -mx-4 sm:mx-0 sm:rounded-2xl overflow-hidden border-b sm:border border-white/10 bg-[#0c0c0e]">
      {profile.cover_url
        ? <img src={profile.cover_url} alt="" data-testid="profile-cover-image" className="h-36 sm:h-52 w-full object-cover" />
        : <PandaCover className="h-36 sm:h-52 w-full" />}
      <div className="px-4 sm:px-6 pb-5">
        <div className="flex items-end justify-between -mt-10 sm:-mt-12">
          <Avatar className="w-20 h-20 sm:w-24 sm:h-24 border-4 border-[#0c0c0e] shadow-xl" data-testid="profile-avatar">
            <AvatarImage src={profile.avatar_url} />
            <AvatarFallback className="bg-rose-600 text-white text-2xl">{name?.[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex items-center gap-2 pb-1">
            <Button onClick={share} variant="ghost" aria-label="Share profile" data-testid="profile-share-button" className="h-9 w-9 p-0 rounded-full border border-white/15 bg-black/40 text-zinc-200 hover:text-white"><Share2 size={14} /></Button>
            {isMe && (
              <Button asChild data-testid="profile-edit-button" className="h-9 rounded-full px-4 bg-white/5 border border-white/15 text-white hover:border-emerald-400">
                <Link to={`/u/${profile.id}/edit`}><Pencil size={13} className="mr-1.5" /> Edit Profile</Link>
              </Button>
            )}
          </div>
        </div>

        <div className="mt-3 space-y-1.5">
          <h1 data-testid="profile-display-name" className="font-display text-2xl sm:text-3xl leading-tight flex items-center gap-2 flex-wrap">{name}<VerifiedBadge show={profile.is_verified} /></h1>
          {!profile.handle_hidden && <div data-testid="profile-handle" className="text-sm text-zinc-500">{profile.display_name}</div>}
          <Badges profile={profile} />
          {profile.bio ? <p data-testid="profile-bio" className="text-sm text-zinc-300 leading-relaxed max-w-2xl pt-1 whitespace-pre-line">{profile.bio}</p> : null}
          <div className="flex items-center gap-4 pt-1 text-sm" data-testid="profile-counts">
            <Link to={`/u/${profile.id}/followers`} className="hover:text-white text-zinc-400"><b className="text-white">{profile.followers_count ?? 0}</b> followers</Link>
            <Link to={`/u/${profile.id}/following`} className="hover:text-white text-zinc-400"><b className="text-white">{profile.following_count ?? 0}</b> following</Link>
            {profile.bamboo_lifetime_earned != null && (profile.is_creator || profile.bamboo_lifetime_earned > 0) && (
              <span className="text-zinc-400" title="Lifetime Bamboo earned from gifts" data-testid="profile-bamboo-earned"><b className="text-white">{Number(profile.bamboo_lifetime_earned).toLocaleString()}</b> 🎋</span>
            )}
          </div>
        </div>

        <div className="mt-4 flex gap-2" data-testid="profile-actions">
          {isMe ? (
            <Button onClick={onCreatePost} data-testid="profile-create-post-button" className="h-11 rounded-xl px-5 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold flex items-center gap-2"><Plus size={16} /> Create Post</Button>
          ) : me ? (
            <>
              <Button onClick={toggleFollow} disabled={busy} data-testid="profile-follow-button" className={`h-11 rounded-xl px-4 sm:px-6 flex-1 sm:flex-none justify-center font-semibold flex items-center gap-2 ${profile.is_followed_by_me ? "bg-transparent border border-emerald-500/60 text-emerald-300 hover:bg-emerald-950/30" : "bg-emerald-500 hover:bg-emerald-400 text-black"}`}>
                {profile.is_followed_by_me ? <><Check size={16} /> Following</> : <><Plus size={16} /> Follow</>}
              </Button>
              <Button onClick={() => navigate(`/inbox?to=${profile.id}`)} data-testid="profile-message-button" className="h-11 rounded-xl px-4 sm:px-5 flex-1 sm:flex-none justify-center bg-white/5 border border-white/15 text-white hover:border-emerald-400 flex items-center gap-2"><MessageSquare size={16} /> Message</Button>
              <Button onClick={() => setGiftOpen(true)} data-testid="profile-gift-button" aria-label="Send a gift" className="h-11 w-11 p-0 rounded-xl bg-white/5 border border-amber-400/40 text-amber-300 hover:bg-amber-500/10"><Gift size={16} /></Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="profile-more-button" aria-label="More actions" className="h-11 w-11 p-0 rounded-xl bg-white/5 border border-white/15 text-white hover:border-emerald-400"><ChevronDown size={16} /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-zinc-950 border-white/10 text-white min-w-[170px]">
                  <DropdownMenuItem data-testid="profile-block-button" onSelect={toggleBlock} className="gap-2 focus:bg-white/10"><Ban size={14} /> {profile.is_blocked_by_me ? "Unblock" : "Block"}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="profile-report-button" onSelect={() => setReportOpen(true)} className="gap-2 text-rose-300 focus:bg-white/10"><Flag size={14} /> Report</DropdownMenuItem>
                  {cc.isAdmin && (
                    <>
                      <DropdownMenuItem data-testid="profile-moderate-button" onSelect={() => cc.openCC({ tab: "member", target: { type: "user", id: profile.id, handle: profile.handle } })} className="gap-2 text-sky-300 focus:bg-white/10"><Shield size={14} /> Moderate</DropdownMenuItem>
                      <DropdownMenuItem data-testid="profile-capture-evidence-button" onSelect={() => cc.openCC({ tab: "member", target: { type: "user", id: profile.id, handle: profile.handle, evidence: true } })} className="gap-2 text-sky-300 focus:bg-white/10"><Camera size={14} /> Capture evidence</DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <GiftPicker open={giftOpen} onOpenChange={setGiftOpen} onSend={sendGift} context="dm" recipientName={profile.handle} />
              <ReportModal open={reportOpen} onOpenChange={setReportOpen} target={{ type: "user", id: profile.id }} onBlocked={onReload} />
              <BlockConfirmDialog open={blockOpen} onOpenChange={setBlockOpen} targetRef={profile.id} name={profile.display_name} onBlocked={onReload} onUndone={onReload} testid="profile-block" />
            </>
          ) : (
            <Button onClick={() => navigate("/login")} data-testid="profile-login-to-follow" className="h-11 rounded-xl px-6 bg-emerald-500 text-black font-semibold">Log in to follow</Button>
          )}
        </div>
      </div>
    </section>
  );
}
